import type { Place } from '../../types/place'
import { getSupabaseOrThrow } from '../supabaseClient'
import { uploadImageToBucket } from './journalPhotoUploads'
import { ensurePlaceFromMock } from './places'

const MAX_BODY_LENGTH = 4000
const MAX_COMMENT_LENGTH = 1000

function looksLikeProfanity(text: string): boolean {
  const t = text.trim().toLowerCase()
  if (!t) return false
  const rx = /(^|[^a-z0-9_ąćęłńóśźż])(kurw|k\*+rw|chuj|huj|pierd|jeba|jeb\*+|skurw|sukinsyn|cwel|dziwk|wypier|spierdal|pierdo)([^a-z0-9_ąćęłńóśźż]|$)/i
  return rx.test(t)
}

export type CommunityPostPhoto = {
  id: string
  url: string
}

export type CommunityPost = {
  id: string
  userId: string
  /** mock_id z public.places (1..N), spójne z `placesMock`. */
  placeId: number
  /** Nazwa miejsca (z public.places.name); UI wyświetla bez extra fetcha. */
  placeName: string
  author: {
    fullName: string | null
    origin: string | null
    avatarUrl: string | null
  } | null
  body: string
  /** Zgłoszenie do konkursu gadżetów (flaga przy publikacji). */
  isContestEntry: boolean
  createdAt: string
  updatedAt: string
  photos: CommunityPostPhoto[]
  /** Liczba polubień (publiczna). */
  likeCount: number
  /** Liczba komentarzy (publiczna). */
  commentCount: number
  /** Czy bieżący użytkownik polubił ten post (false dla anonima). */
  likedByMe: boolean
}

export type CommunityPostComment = {
  id: string
  postId: string
  userId: string
  body: string
  createdAt: string
  updatedAt: string
  author: {
    fullName: string | null
    origin: string | null
    avatarUrl: string | null
  } | null
}

type PostRow = {
  id: string
  user_id: string
  place_id: string
  body: string
  is_contest_entry: boolean | null
  created_at: string
  updated_at: string
}

type PlaceRow = { id: string; mock_id: number | null; slug: string | null; name: string }
type PhotoRow = { id: string; post_id: string; url: string; position: number; created_at: string }

/** Wiersz posta z joinem PostgREST (oszczędza osobne zapytanie do community_post_photos). */
type PostRowWithNestedPhotos = PostRow & {
  community_post_photos?: PhotoRow[] | null
}

const COMMUNITY_POST_SELECT_WITH_PHOTOS = `
  id, user_id, place_id, body, is_contest_entry, created_at, updated_at,
  community_post_photos (
    id,
    post_id,
    url,
    position,
    created_at
  )
`.trim()

function photoMapFromNestedPostRows(rows: PostRowWithNestedPhotos[]): Map<string, CommunityPostPhoto[]> {
  const photoMap = new Map<string, CommunityPostPhoto[]>()
  for (const r of rows) {
    const list = (r.community_post_photos ?? [])
      .slice()
      .sort((a, b) => {
        if (a.position !== b.position) return a.position - b.position
        return String(a.created_at).localeCompare(String(b.created_at))
      })
      .map((p) => ({ id: p.id, url: p.url }))
    photoMap.set(r.id, list)
  }
  return photoMap
}

function stripNestedPhotos(rows: PostRowWithNestedPhotos[]): PostRow[] {
  return rows.map(({ community_post_photos: _nested, ...rest }) => rest)
}

function parsePlaceMockId(p: { mock_id: number | null; slug: string | null }): number | null {
  if (typeof p.mock_id === 'number' && p.mock_id > 0) return p.mock_id
  if (p.slug?.startsWith('place-')) {
    const n = Number(p.slug.slice('place-'.length))
    if (Number.isFinite(n) && n > 0) return n
  }
  return null
}

type CommunityProfilePublic = {
  fullName: string | null
  origin: string | null
  avatarUrl: string | null
}

async function enrichWithProfiles(userIds: string[]): Promise<Map<string, CommunityProfilePublic>> {
  const supabase = getSupabaseOrThrow()
  const unique = Array.from(new Set(userIds.map((x) => x.trim()).filter(Boolean)))
  if (!unique.length) return new Map()

  const { data, error } = await supabase.rpc('community_profiles_for_ids', { p_ids: unique })
  if (error) throw new Error(error.message)

  const rows = (data ?? []) as Array<{
    id: string
    full_name: string | null
    origin: string | null
    avatar_url?: string | null
  }>
  return new Map(
    rows.map((r) => [
      r.id,
      {
        fullName: r.full_name,
        origin: r.origin,
        avatarUrl: r.avatar_url ?? null,
      },
    ]),
  )
}

async function loadPlacesByDbIds(dbIds: string[]): Promise<Map<string, PlaceRow>> {
  const unique = Array.from(new Set(dbIds.filter(Boolean)))
  if (!unique.length) return new Map()
  const supabase = getSupabaseOrThrow()
  const { data, error } = await supabase
    .from('places')
    .select('id, mock_id, slug, name')
    .in('id', unique)
  if (error) throw new Error(error.message)
  const rows = (data ?? []) as PlaceRow[]
  return new Map(rows.map((r) => [r.id, r]))
}

type EngagementRow = {
  post_id: string
  like_count: number | string
  comment_count: number | string
  liked_by_me: boolean
}

type EngagementInfo = { likeCount: number; commentCount: number; likedByMe: boolean }

const EMPTY_ENGAGEMENT: EngagementInfo = { likeCount: 0, commentCount: 0, likedByMe: false }

async function loadEngagementForPosts(
  postIds: string[],
  currentUserId: string | null,
): Promise<Map<string, EngagementInfo>> {
  const map = new Map<string, EngagementInfo>()
  if (!postIds.length) return map
  const supabase = getSupabaseOrThrow()
  const { data, error } = await supabase.rpc('community_engagement_for_posts', {
    p_post_ids: postIds,
    p_user_id: currentUserId,
  })
  if (error) throw new Error(error.message)
  const rows = (data ?? []) as EngagementRow[]
  for (const r of rows) {
    map.set(r.post_id, {
      likeCount: Number(r.like_count) || 0,
      commentCount: Number(r.comment_count) || 0,
      likedByMe: Boolean(r.liked_by_me),
    })
  }
  return map
}

async function getCurrentUserId(): Promise<string | null> {
  // getSession() korzysta z lokalnego cache klienta , zero round-tripów do serwera.
  const supabase = getSupabaseOrThrow()
  const { data } = await supabase.auth.getSession()
  return data?.session?.user?.id ?? null
}

function rowsToPosts(
  rows: PostRow[],
  placeMap: Map<string, PlaceRow>,
  profileMap: Map<string, CommunityProfilePublic>,
  photoMap: Map<string, CommunityPostPhoto[]>,
  engagementMap: Map<string, EngagementInfo>,
): CommunityPost[] {
  return rows
    .map((r) => {
      const place = placeMap.get(r.place_id)
      if (!place) return null
      const mockId = parsePlaceMockId(place)
      if (!mockId) return null
      const eng = engagementMap.get(r.id) ?? EMPTY_ENGAGEMENT
      return {
        id: r.id,
        userId: r.user_id,
        placeId: mockId,
        placeName: place.name,
        author: profileMap.get(r.user_id) ?? null,
        body: r.body,
        isContestEntry: Boolean(r.is_contest_entry),
        createdAt: r.created_at,
        updatedAt: r.updated_at,
        photos: photoMap.get(r.id) ?? [],
        likeCount: eng.likeCount,
        commentCount: eng.commentCount,
        likedByMe: eng.likedByMe,
      } satisfies CommunityPost
    })
    .filter(Boolean) as CommunityPost[]
}

export type CommunityFeedPage = {
  posts: CommunityPost[]
  /** ISO timestamp ostatniego rekordu , przekaż jako `cursor` przy doładowaniu kolejnej strony. */
  nextCursor: string | null
}

export type CommunityLeaderboardEntry = {
  rank: number
  userId: string
  fullName: string | null
  origin: string | null
  avatarUrl: string | null
  pointsTotal: number
  isMe: boolean
}

export type CommunityLeaderboard = {
  seasonId: string | null
  seasonTitle: string | null
  seasonStatus: 'active' | 'closed' | null
  entries: CommunityLeaderboardEntry[]
  myEntry: CommunityLeaderboardEntry | null
}

export type CommunityMyPoints = {
  userId: string | null
  pointsTotal: number
  rank: number | null
  seasonId: string | null
  seasonTitle: string | null
  seasonStatus: 'active' | 'closed' | null
}

/**
 * Publiczny feed społeczności. Czyta każdy (anon + authenticated) , patrz polityki RLS.
 * Paginacja kursorowa po `created_at` (descending).
 */
export async function listCommunityFeed(input?: {
  limit?: number
  /** ISO timestamp z poprzedniej strony (created_at), zwraca posty starsze od kursora. */
  cursor?: string | null
  /** Filtr po `placesMock.id` , UI wybiera z lokalnego katalogu. */
  placeMockId?: number | null
}): Promise<CommunityFeedPage> {
  const supabase = getSupabaseOrThrow()
  const limit = Math.max(1, Math.min(50, input?.limit ?? 12))

  let placeFilterDbId: string | null = null
  if (typeof input?.placeMockId === 'number' && input.placeMockId > 0) {
    const { data, error } = await supabase
      .from('places')
      .select('id')
      .eq('mock_id', input.placeMockId)
      .maybeSingle()
    if (error) throw new Error(error.message)
    placeFilterDbId = (data as { id: string } | null)?.id ?? null
    // Brak takiego miejsca w bazie ⇒ pusty feed (zamiast rzucać błąd).
    if (!placeFilterDbId) return { posts: [], nextCursor: null }
  }

  let query = supabase
    .from('community_posts')
    .select(COMMUNITY_POST_SELECT_WITH_PHOTOS)
    .order('created_at', { ascending: false })
    .limit(limit)

  if (input?.cursor) query = query.lt('created_at', input.cursor)
  if (placeFilterDbId) query = query.eq('place_id', placeFilterDbId)

  const { data, error } = await query
  if (error) throw new Error(error.message)
  const rawRows = (data ?? []) as unknown as PostRowWithNestedPhotos[]
  if (!rawRows.length) return { posts: [], nextCursor: null }

  const rows = stripNestedPhotos(rawRows)
  const photoMap = photoMapFromNestedPostRows(rawRows)

  const currentUserId = await getCurrentUserId()
  const postIds = rows.map((r) => r.id)

  const [placeMap, profileMap, engagementMap] = await Promise.all([
    loadPlacesByDbIds(rows.map((r) => r.place_id)),
    enrichWithProfiles(rows.map((r) => r.user_id)),
    loadEngagementForPosts(postIds, currentUserId),
  ])

  const posts = rowsToPosts(rows, placeMap, profileMap, photoMap, engagementMap)
  const nextCursor = rows.length === limit ? rows[rows.length - 1].created_at : null
  return { posts, nextCursor }
}

type LeaderboardRow = {
  rank: number | string
  user_id: string
  full_name: string | null
  origin: string | null
  avatar_url?: string | null
  points_total: number | string
  is_me: boolean
  season_id: string | null
  season_title: string | null
  season_status: 'active' | 'closed' | null
}

export async function listCommunityLeaderboard(limit = 20): Promise<CommunityLeaderboard> {
  const supabase = getSupabaseOrThrow()
  const safeLimit = Math.max(1, Math.min(100, limit))
  const { data, error } = await supabase.rpc('community_leaderboard', {
    p_limit: safeLimit,
    p_offset: 0,
    p_season_id: null,
  })
  if (error) throw new Error(error.message)
  const rows = (data ?? []) as LeaderboardRow[]
  const entries: CommunityLeaderboardEntry[] = rows.map((r) => ({
    rank: Number(r.rank) || 0,
    userId: r.user_id,
    fullName: r.full_name,
    origin: r.origin,
    avatarUrl: r.avatar_url ?? null,
    pointsTotal: Number(r.points_total) || 0,
    isMe: Boolean(r.is_me),
  }))
  const first = rows[0]
  return {
    seasonId: first?.season_id ?? null,
    seasonTitle: first?.season_title ?? null,
    seasonStatus: first?.season_status ?? null,
    myEntry: entries.find((e) => e.isMe) ?? null,
    entries,
  }
}

export type CommunityRecentMember = {
  userId: string
  fullName: string | null
  origin: string | null
  avatarUrl: string | null
  joinedAt: string
}

type RecentMemberRow = {
  user_id: string
  full_name: string | null
  origin: string | null
  avatar_url?: string | null
  joined_at: string
}

/** Ostatnio dołączeni uczestnicy (wymaga RPC `community_recent_members` w bazie). */
export async function listCommunityRecentMembers(limit = 8): Promise<CommunityRecentMember[]> {
  const supabase = getSupabaseOrThrow()
  const safeLimit = Math.max(1, Math.min(20, limit))
  const { data, error } = await supabase.rpc('community_recent_members', { p_limit: safeLimit })
  if (error) throw new Error(error.message)
  return ((data ?? []) as RecentMemberRow[]).map((r) => ({
    userId: r.user_id,
    fullName: r.full_name,
    origin: r.origin,
    avatarUrl: r.avatar_url ?? null,
    joinedAt: r.joined_at,
  }))
}

type MyPointsRow = {
  user_id: string | null
  points_total: number | string
  rank: number | string | null
  season_id: string | null
  season_title: string | null
  season_status: 'active' | 'closed' | null
}

export async function getMyCommunityPoints(): Promise<CommunityMyPoints> {
  const supabase = getSupabaseOrThrow()
  const { data, error } = await supabase.rpc('community_my_points', { p_season_id: null })
  if (error) throw new Error(error.message)
  const row = ((data ?? [])[0] ?? null) as MyPointsRow | null
  return {
    userId: row?.user_id ?? null,
    pointsTotal: Number(row?.points_total ?? 0) || 0,
    rank: row?.rank === null || row?.rank === undefined ? null : Number(row.rank) || null,
    seasonId: row?.season_id ?? null,
    seasonTitle: row?.season_title ?? null,
    seasonStatus: row?.season_status ?? null,
  }
}

/** Lista własnych postów (panel użytkownika). RLS i tak zwęża, filtrujemy też klientem dla pewności. */
export async function listMyCommunityPosts(userId: string): Promise<CommunityPost[]> {
  const supabase = getSupabaseOrThrow()
  const { data, error } = await supabase
    .from('community_posts')
    .select(COMMUNITY_POST_SELECT_WITH_PHOTOS)
    .eq('user_id', userId)
    .order('created_at', { ascending: false })
    .limit(200)
  if (error) throw new Error(error.message)
  const rawRows = (data ?? []) as unknown as PostRowWithNestedPhotos[]
  if (!rawRows.length) return []

  const rows = stripNestedPhotos(rawRows)
  const photoMap = photoMapFromNestedPostRows(rawRows)

  const postIds = rows.map((r) => r.id)
  const [placeMap, profileMap, engagementMap] = await Promise.all([
    loadPlacesByDbIds(rows.map((r) => r.place_id)),
    enrichWithProfiles([userId]),
    loadEngagementForPosts(postIds, userId),
  ])

  return rowsToPosts(rows, placeMap, profileMap, photoMap, engagementMap)
}

/** Lista postów dla panelu admina. Admin ma w SQL pełny select przez publiczny feed + polityki moderacji. */
export async function listAdminCommunityPosts(limit = 500): Promise<CommunityPost[]> {
  const supabase = getSupabaseOrThrow()
  const { data, error } = await supabase
    .from('community_posts')
    .select(COMMUNITY_POST_SELECT_WITH_PHOTOS)
    .order('created_at', { ascending: false })
    .limit(Math.max(1, Math.min(1000, limit)))
  if (error) throw new Error(error.message)
  const rawRows = (data ?? []) as unknown as PostRowWithNestedPhotos[]
  if (!rawRows.length) return []

  const rows = stripNestedPhotos(rawRows)
  const photoMap = photoMapFromNestedPostRows(rawRows)

  const currentUserId = await getCurrentUserId()
  const postIds = rows.map((r) => r.id)
  const [placeMap, profileMap, engagementMap] = await Promise.all([
    loadPlacesByDbIds(rows.map((r) => r.place_id)),
    enrichWithProfiles(rows.map((r) => r.user_id)),
    loadEngagementForPosts(postIds, currentUserId),
  ])

  return rowsToPosts(rows, placeMap, profileMap, photoMap, engagementMap)
}

function assertBody(body: string): string {
  const trimmed = body.trim()
  if (!trimmed) throw new Error('Treść posta nie może być pusta.')
  if (trimmed.length > MAX_BODY_LENGTH) {
    throw new Error(`Treść posta może mieć maksymalnie ${MAX_BODY_LENGTH} znaków.`)
  }
  if (looksLikeProfanity(trimmed)) {
    throw new Error('Nie pozwalamy na posty z przekleństwami.')
  }
  return trimmed
}

/**
 * Tworzy nowy post w społeczności. Miejsce jest wymagane , musi pochodzić z katalogu (placesMock).
 * Pliki są opcjonalne; jeżeli są, wgrywamy je do bucketa journal-photos pod prefixem
 * `community/{userId}/{postId}` i tworzymy wpisy w `community_post_photos`.
 */
export async function createCommunityPost(input: {
  userId: string
  place: Place
  body: string
  files?: File[] | null
  /** Gotowe publiczne URL-e (np. zdjęcia z dziennika), bez ponownego uploadu. */
  photoUrls?: string[] | null
  isContestEntry?: boolean
}): Promise<CommunityPost> {
  const supabase = getSupabaseOrThrow()
  const trimmed = assertBody(input.body)
  const dbPlace = await ensurePlaceFromMock(input.place)
  const isContestEntry = Boolean(input.isContestEntry)

  const { data: postData, error: insertErr } = await supabase
    .from('community_posts')
    .insert({
      user_id: input.userId,
      place_id: dbPlace.id,
      body: trimmed,
      is_contest_entry: isContestEntry,
    })
    .select('id, user_id, place_id, body, is_contest_entry, created_at, updated_at')
    .single()
  if (insertErr) throw new Error(insertErr.message)
  const post = postData as PostRow

  const photos: CommunityPostPhoto[] = []
  let nextPosition = 0

  const existingUrls = (input.photoUrls ?? [])
    .map((u) => u.trim())
    .filter((u) => u.startsWith('http://') || u.startsWith('https://'))
  for (const url of existingUrls) {
    const { data: photoRow, error: photoErr } = await supabase
      .from('community_post_photos')
      .insert({ post_id: post.id, url, position: nextPosition })
      .select('id, url')
      .single()
    if (photoErr) throw new Error(photoErr.message)
    photos.push({ id: (photoRow as { id: string }).id, url: (photoRow as { url: string }).url })
    nextPosition += 1
  }

  const files = (input.files ?? []).filter((f): f is File => Boolean(f))
  if (files.length > 0) {
    const pathPrefix = `community/${input.userId}/${post.id}`
    for (const file of files) {
      const uploaded = await uploadImageToBucket({ pathPrefix, file })
      const { data: photoRow, error: photoErr } = await supabase
        .from('community_post_photos')
        .insert({ post_id: post.id, url: uploaded.publicUrl, position: nextPosition })
        .select('id, url')
        .single()
      if (photoErr) throw new Error(photoErr.message)
      photos.push({ id: (photoRow as { id: string }).id, url: (photoRow as { url: string }).url })
      nextPosition += 1
    }
  }

  return {
    id: post.id,
    userId: post.user_id,
    placeId: input.place.id,
    placeName: dbPlace.name,
    author: null,
    body: post.body,
    isContestEntry: Boolean(post.is_contest_entry),
    createdAt: post.created_at,
    updatedAt: post.updated_at,
    photos,
    likeCount: 0,
    commentCount: 0,
    likedByMe: false,
  }
}

export async function updateMyCommunityPost(input: { id: string; userId: string; body: string }): Promise<void> {
  const supabase = getSupabaseOrThrow()
  const trimmed = assertBody(input.body)
  const { error } = await supabase
    .from('community_posts')
    .update({ body: trimmed })
    .eq('id', input.id)
    .eq('user_id', input.userId)
  if (error) throw new Error(error.message)
}

export async function updateAdminCommunityPost(input: { id: string; body: string }): Promise<void> {
  const supabase = getSupabaseOrThrow()
  const trimmed = assertBody(input.body)
  const { error } = await supabase.from('community_posts').update({ body: trimmed }).eq('id', input.id)
  if (error) throw new Error(error.message)
}

export async function deleteMyCommunityPost(input: { id: string; userId: string }): Promise<void> {
  const supabase = getSupabaseOrThrow()
  // FK cascade w SQL usunie powiązane community_post_photos. Plików w Storage
  // celowo nie sprzątamy w MVP (brak takiego cleanupa w istniejących wzorcach).
  const { error } = await supabase
    .from('community_posts')
    .delete()
    .eq('id', input.id)
    .eq('user_id', input.userId)
  if (error) throw new Error(error.message)
}

export async function deleteAdminCommunityPost(id: string): Promise<void> {
  const supabase = getSupabaseOrThrow()
  const { error } = await supabase.from('community_posts').delete().eq('id', id)
  if (error) throw new Error(error.message)
}

/** Doda zdjęcie do istniejącego własnego posta. Pozycja: na końcu listy. */
export async function addPhotoToMyCommunityPost(input: {
  userId: string
  postId: string
  file: File
}): Promise<CommunityPostPhoto> {
  const supabase = getSupabaseOrThrow()
  const pathPrefix = `community/${input.userId}/${input.postId}`
  const uploaded = await uploadImageToBucket({ pathPrefix, file: input.file })

  const { data: maxPosData } = await supabase
    .from('community_post_photos')
    .select('position')
    .eq('post_id', input.postId)
    .order('position', { ascending: false })
    .limit(1)
    .maybeSingle()
  const nextPosition =
    typeof (maxPosData as { position?: number } | null)?.position === 'number'
      ? ((maxPosData as { position: number }).position ?? 0) + 1
      : 0

  const { data, error } = await supabase
    .from('community_post_photos')
    .insert({ post_id: input.postId, url: uploaded.publicUrl, position: nextPosition })
    .select('id, url')
    .single()
  if (error) throw new Error(error.message)
  const row = data as { id: string; url: string }
  return { id: row.id, url: row.url }
}

export async function deleteMyCommunityPostPhoto(input: { photoId: string }): Promise<void> {
  const supabase = getSupabaseOrThrow()
  // RLS gwarantuje, że niewłaściciel nie usunie cudzego zdjęcia.
  const { error } = await supabase.from('community_post_photos').delete().eq('id', input.photoId)
  if (error) throw new Error(error.message)
}

export async function deleteAdminCommunityPostPhoto(photoId: string): Promise<void> {
  const supabase = getSupabaseOrThrow()
  const { error } = await supabase.from('community_post_photos').delete().eq('id', photoId)
  if (error) throw new Error(error.message)
}

/**
 * Zwraca najnowszy post w społeczności tworzony przez `userId` dla podanego miejsca
 * (po `placesMock.id` → `places.mock_id`). Używane w karcie "Opublikuj w społeczności"
 * w panelu szczegółów miejsca, żeby wykryć stan: opublikowano / jeszcze nie.
 */
export async function findMyCommunityPostForPlace(input: {
  userId: string
  placeMockId: number
}): Promise<CommunityPost | null> {
  const supabase = getSupabaseOrThrow()

  const { data: placeData, error: placeErr } = await supabase
    .from('places')
    .select('id')
    .eq('mock_id', input.placeMockId)
    .maybeSingle()
  if (placeErr) throw new Error(placeErr.message)
  const placeDbId = (placeData as { id: string } | null)?.id
  if (!placeDbId) return null

  const { data, error } = await supabase
    .from('community_posts')
    .select(COMMUNITY_POST_SELECT_WITH_PHOTOS)
    .eq('user_id', input.userId)
    .eq('place_id', placeDbId)
    .order('created_at', { ascending: false })
    .limit(1)
    .maybeSingle()
  if (error) throw new Error(error.message)
  if (!data) return null

  const rawRow = data as unknown as PostRowWithNestedPhotos
  const row = stripNestedPhotos([rawRow])[0]
  const photoMap = photoMapFromNestedPostRows([rawRow])
  const [placeMap, profileMap, engagementMap] = await Promise.all([
    loadPlacesByDbIds([row.place_id]),
    enrichWithProfiles([row.user_id]),
    loadEngagementForPosts([row.id], input.userId),
  ])
  const posts = rowsToPosts([row], placeMap, profileMap, photoMap, engagementMap)
  return posts[0] ?? null
}

// ======================== LIKES ========================

/**
 * Toggle polubienia. Jeśli `currentlyLiked` jest podane, używamy go żeby zaoszczędzić round-trip;
 * w przeciwnym razie wyciągamy stan z DB. Zwraca nowy stan + delta licznika.
 */
export async function toggleCommunityPostLike(input: {
  postId: string
  userId: string
  currentlyLiked?: boolean | null
}): Promise<{ liked: boolean; delta: 1 | -1 }> {
  const supabase = getSupabaseOrThrow()

  let isLiked = input.currentlyLiked ?? null
  if (isLiked === null) {
    const { data, error } = await supabase
      .from('community_post_likes')
      .select('post_id')
      .eq('post_id', input.postId)
      .eq('user_id', input.userId)
      .maybeSingle()
    if (error) throw new Error(error.message)
    isLiked = Boolean(data)
  }

  if (isLiked) {
    const { error } = await supabase
      .from('community_post_likes')
      .delete()
      .eq('post_id', input.postId)
      .eq('user_id', input.userId)
    if (error) throw new Error(error.message)
    return { liked: false, delta: -1 }
  } else {
    const { error } = await supabase
      .from('community_post_likes')
      .insert({ post_id: input.postId, user_id: input.userId })
    if (error) {
      // Idempotencja: jeśli wpis już jest (race), traktuj jak liked.
      if (/duplicate key|conflict/i.test(error.message)) {
        return { liked: true, delta: 0 as unknown as 1 }
      }
      throw new Error(error.message)
    }
    return { liked: true, delta: 1 }
  }
}

// ======================== COMMENTS ========================

type CommentRow = {
  id: string
  post_id: string
  user_id: string
  body: string
  created_at: string
  updated_at: string
}

function assertCommentBody(body: string): string {
  const trimmed = body.trim()
  if (!trimmed) throw new Error('Treść komentarza nie może być pusta.')
  if (trimmed.length > MAX_COMMENT_LENGTH) {
    throw new Error(`Komentarz może mieć maksymalnie ${MAX_COMMENT_LENGTH} znaków.`)
  }
  if (looksLikeProfanity(trimmed)) {
    throw new Error('Nie pozwalamy na komentarze z przekleństwami.')
  }
  return trimmed
}

/** Lista komentarzy posortowana rosnąco (najstarsze pierwsze). Czytanie publiczne. */
export async function listCommunityPostComments(postId: string): Promise<CommunityPostComment[]> {
  const supabase = getSupabaseOrThrow()
  const { data, error } = await supabase
    .from('community_post_comments')
    .select('id, post_id, user_id, body, created_at, updated_at')
    .eq('post_id', postId)
    .order('created_at', { ascending: true })
    .limit(500)
  if (error) throw new Error(error.message)
  const rows = (data ?? []) as CommentRow[]
  if (!rows.length) return []

  const profileMap = await enrichWithProfiles(rows.map((r) => r.user_id))
  return rows.map((r) => ({
    id: r.id,
    postId: r.post_id,
    userId: r.user_id,
    body: r.body,
    createdAt: r.created_at,
    updatedAt: r.updated_at,
    author: profileMap.get(r.user_id) ?? null,
  }))
}

export async function addCommunityPostComment(input: {
  postId: string
  userId: string
  body: string
}): Promise<CommunityPostComment> {
  const supabase = getSupabaseOrThrow()
  const trimmed = assertCommentBody(input.body)
  const { data, error } = await supabase
    .from('community_post_comments')
    .insert({ post_id: input.postId, user_id: input.userId, body: trimmed })
    .select('id, post_id, user_id, body, created_at, updated_at')
    .single()
  if (error) throw new Error(error.message)
  const r = data as CommentRow
  const profileMap = await enrichWithProfiles([r.user_id])
  return {
    id: r.id,
    postId: r.post_id,
    userId: r.user_id,
    body: r.body,
    createdAt: r.created_at,
    updatedAt: r.updated_at,
    author: profileMap.get(r.user_id) ?? null,
  }
}

export async function updateMyCommunityPostComment(input: {
  id: string
  userId: string
  body: string
}): Promise<void> {
  const supabase = getSupabaseOrThrow()
  const trimmed = assertCommentBody(input.body)
  const { error } = await supabase
    .from('community_post_comments')
    .update({ body: trimmed })
    .eq('id', input.id)
    .eq('user_id', input.userId)
  if (error) throw new Error(error.message)
}

export async function deleteMyCommunityPostComment(input: { id: string; userId: string }): Promise<void> {
  const supabase = getSupabaseOrThrow()
  const { error } = await supabase
    .from('community_post_comments')
    .delete()
    .eq('id', input.id)
    .eq('user_id', input.userId)
  if (error) throw new Error(error.message)
}

export async function updateAdminCommunityPostComment(input: { id: string; body: string }): Promise<void> {
  const supabase = getSupabaseOrThrow()
  const trimmed = assertCommentBody(input.body)
  const { error } = await supabase.from('community_post_comments').update({ body: trimmed }).eq('id', input.id)
  if (error) throw new Error(error.message)
}

export async function deleteAdminCommunityPostComment(id: string): Promise<void> {
  const supabase = getSupabaseOrThrow()
  const { error } = await supabase.from('community_post_comments').delete().eq('id', id)
  if (error) throw new Error(error.message)
}
